import { useEffect, useMemo, useRef, useState } from "react";
import { toast } from "sonner";
import { Boxes, Download, Upload, ShieldAlert, FileText, ExternalLink, CheckCircle2, AlertTriangle, XCircle, RefreshCw } from "lucide-react";
import api, { apiError } from "../lib/api";
import { Button } from "../components/ui/button";
import { Checkbox } from "../components/ui/checkbox";
import { Switch } from "../components/ui/switch";

const REC_STYLES = {
  adoptar: { icon: CheckCircle2, cls: "border-emerald-300 bg-emerald-50 text-emerald-900" },
  revisar: { icon: AlertTriangle, cls: "border-amber-300 bg-amber-50 text-amber-900" },
  rechazar: { icon: XCircle, cls: "border-red-300 bg-red-50 text-red-900" },
};

const ACTION_BADGE = {
  nuevo: "bg-blue-100 text-blue-900 border-blue-300",
  actualizacion: "bg-emerald-100 text-emerald-900 border-emerald-300",
  duplicado: "bg-neutral-200 text-neutral-700 border-neutral-300",
  conflicto: "bg-red-100 text-red-900 border-red-300",
};

function Warning() {
  return (
    <div className="border border-amber-300 bg-amber-50 text-amber-900 rounded-lg p-3 flex items-start gap-2" data-testid="scme-warning">
      <ShieldAlert size={18} className="mt-0.5 shrink-0" />
      <div className="text-xs leading-relaxed">
        <div className="font-bold uppercase tracking-wider text-[11px]">Importación = análisis</div>
        La importación <strong>no instala</strong>, <strong>no copia código</strong> ni <strong>modifica</strong> el catálogo. Genera únicamente un informe de compatibilidad. Cualquier adopción de plugins debe ser <strong>manual y posterior</strong>.
      </div>
    </div>
  );
}

function RegistryTab({ registry }) {
  if (!registry) return <div className="text-sm text-muted-foreground">Cargando…</div>;
  const plugins = registry.plugins || [];
  return (
    <div className="space-y-3">
      <div className="text-sm text-muted-foreground">
        Catálogo local · Proyecto <strong>{registry.project}</strong> · <strong>{plugins.length}</strong> plugin{plugins.length === 1 ? "" : "s"} registrado{plugins.length === 1 ? "" : "s"}
      </div>
      <div className="border border-border rounded-lg overflow-hidden" data-testid="scme-registry-table">
        <table className="w-full text-sm">
          <thead className="bg-secondary/50 text-xs uppercase tracking-wider text-muted-foreground">
            <tr>
              <th className="text-left px-3 py-2 font-semibold">Plugin</th>
              <th className="text-left px-3 py-2 font-semibold">Versión</th>
              <th className="text-left px-3 py-2 font-semibold">Categoría</th>
              <th className="text-left px-3 py-2 font-semibold">Reutilización</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-border">
            {plugins.map((p) => (
              <tr key={p.id} data-testid={`scme-plugin-${p.id}`}>
                <td className="px-3 py-2">
                  <div className="font-medium">{p.name}</div>
                  <div className="text-[11px] text-muted-foreground">{p.id}</div>
                </td>
                <td className="px-3 py-2 tabular font-mono text-xs">{p.version}</td>
                <td className="px-3 py-2 text-xs">{p.category}</td>
                <td className="px-3 py-2 text-xs">{p.reuse_potential}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

function ExportTab({ registry }) {
  const plugins = registry?.plugins || [];
  const [selected, setSelected] = useState({});
  const [opts, setOpts] = useState({ include_dependency_map: true, include_reuse_matrix: true, include_catalog: true });
  const [busy, setBusy] = useState(false);

  const allChecked = plugins.length > 0 && plugins.every((p) => selected[p.id]);
  const toggleAll = () => {
    if (allChecked) setSelected({});
    else setSelected(Object.fromEntries(plugins.map((p) => [p.id, true])));
  };
  const selectedIds = Object.keys(selected).filter((k) => selected[k]);

  const preview = useMemo(() => ({
    scme_version: "1.0",
    project: registry?.project,
    plugin_count: selectedIds.length || plugins.length,
    plugins: (selectedIds.length ? plugins.filter((p) => selected[p.id]) : plugins).map((p) => ({ id: p.id, name: p.name, version: p.version })),
  }), [registry, selected, selectedIds, plugins]);

  const generate = async () => {
    setBusy(true);
    try {
      const body = { plugin_ids: selectedIds.length ? selectedIds : null, ...opts };
      const res = await api.post("/scme/export", body, { responseType: "blob" });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement("a");
      a.href = url;
      const disp = res.headers["content-disposition"] || "";
      const m = /filename="([^"]+)"/.exec(disp);
      a.download = m ? m[1] : "SCME_EXPORT.zip";
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
      toast.success("SCME_EXPORT.zip descargado");
    } catch (e) {
      toast.error(apiError(e));
    } finally { setBusy(false); }
  };

  return (
    <div className="space-y-4">
      <Warning />
      <div>
        <div className="text-xs uppercase tracking-wider text-muted-foreground font-bold mb-2">Plugins a incluir</div>
        <div className="border border-border rounded-lg divide-y divide-border max-h-[300px] overflow-y-auto">
          <label className="flex items-center gap-2 px-3 py-2 bg-secondary/40 cursor-pointer" data-testid="scme-export-toggle-all">
            <Checkbox checked={allChecked} onCheckedChange={toggleAll} />
            <span className="text-sm font-semibold">Seleccionar todos ({plugins.length})</span>
          </label>
          {plugins.map((p) => (
            <label key={p.id} className="flex items-center gap-2 px-3 py-2 cursor-pointer hover:bg-secondary/30">
              <Checkbox data-testid={`scme-export-cb-${p.id}`} checked={!!selected[p.id]} onCheckedChange={(v) => setSelected({ ...selected, [p.id]: !!v })} />
              <div className="flex-1 min-w-0">
                <div className="text-sm truncate">{p.name} <span className="text-muted-foreground text-xs">· v{p.version}</span></div>
              </div>
            </label>
          ))}
        </div>
        <div className="text-xs text-muted-foreground mt-1">Sin selección → se exportan todos.</div>
      </div>

      <div className="space-y-2">
        <div className="text-xs uppercase tracking-wider text-muted-foreground font-bold">Opciones</div>
        {[
          ["include_dependency_map", "Incluir DEPENDENCY_MAP.json"],
          ["include_reuse_matrix", "Incluir REUSE_MATRIX.json"],
          ["include_catalog", "Incluir documentación (PLUGIN_CATALOG.md + SCME_STANDARD.md)"],
        ].map(([key, label]) => (
          <label key={key} className="flex items-center justify-between border border-border rounded-lg px-3 py-2">
            <span className="text-sm">{label}</span>
            <Switch data-testid={`scme-opt-${key}`} checked={opts[key]} onCheckedChange={(v) => setOpts({ ...opts, [key]: v })} />
          </label>
        ))}
      </div>

      <div>
        <div className="text-xs uppercase tracking-wider text-muted-foreground font-bold mb-1">Vista previa del manifest</div>
        <pre className="bg-secondary/40 border border-border rounded-lg p-3 text-[11px] max-h-48 overflow-auto" data-testid="scme-export-preview">
{JSON.stringify(preview, null, 2)}
        </pre>
      </div>

      <Button data-testid="scme-export-generate" className="w-full" onClick={generate} disabled={busy}>
        <Download size={16} className="mr-2" /> {busy ? "Generando…" : "Generar SCME_EXPORT.zip"}
      </Button>
      <div className="text-[11px] text-muted-foreground">Ningún dato sensible se incluye (whitelist estricta filtra passwords, tokens, importes, IBAN, DNI…). Firma SHA-256 en el manifest para verificar integridad.</div>
    </div>
  );
}

function ReportView({ report }) {
  if (!report) return null;
  const Rec = REC_STYLES[report.recommendation] || REC_STYLES.revisar;
  return (
    <div className="space-y-3" data-testid="scme-analysis-report">
      <div className={`border rounded-lg p-3 flex items-start gap-3 ${Rec.cls}`}>
        <Rec.icon size={20} className="shrink-0 mt-0.5" />
        <div>
          <div className="text-xs uppercase tracking-wider font-bold">Recomendación</div>
          <div className="font-heading text-lg font-extrabold">{report.recommendation.toUpperCase()}</div>
          <div className="text-sm">{report.recommendation_reason}</div>
        </div>
      </div>

      <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
        {[
          ["nuevo", "Nuevos"], ["actualizacion", "Actualizaciones"], ["duplicado", "Duplicados"], ["conflicto", "Conflictos"],
        ].map(([k, label]) => (
          <div key={k} className="border border-border rounded-lg p-3 text-center">
            <div className="text-[10px] uppercase tracking-wider text-muted-foreground font-bold">{label}</div>
            <div className="text-2xl font-semibold tabular">{report.counts?.[k] || 0}</div>
          </div>
        ))}
      </div>

      <div className="grid grid-cols-2 gap-2 text-xs text-muted-foreground">
        <div>SHA-256 del ZIP: <span className="font-mono">{(report.zip_sha256 || "").slice(0, 16)}…</span></div>
        <div>Integridad manifest: {report.manifest_integrity_ok === true ? "✓ OK" : report.manifest_integrity_ok === false ? "✗ FALLA" : "—"}</div>
        <div>Tamaño: {report.zip_size} bytes</div>
        <div>Ficheros: {(report.structural?.ok ? "estructura válida" : "estructura inválida")}</div>
      </div>

      {report.structural && !report.structural.ok && (
        <div className="border border-red-300 bg-red-50 text-red-900 rounded-lg p-3 text-sm">
          <div className="font-bold">Errores estructurales</div>
          <ul className="list-disc pl-5 mt-1 text-xs">
            {(report.structural.errors || []).map((e, i) => <li key={i}>{e}</li>)}
          </ul>
        </div>
      )}

      <div className="border border-border rounded-lg overflow-hidden">
        <table className="w-full text-sm">
          <thead className="bg-secondary/50 text-xs uppercase tracking-wider text-muted-foreground">
            <tr>
              <th className="text-left px-3 py-2 font-semibold">Plugin</th>
              <th className="text-left px-3 py-2 font-semibold">Acción</th>
              <th className="text-left px-3 py-2 font-semibold">Versión</th>
              <th className="text-left px-3 py-2 font-semibold">Razones</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-border">
            {(report.matches || []).map((m) => (
              <tr key={m.plugin_id} data-testid={`scme-match-${m.plugin_id}`}>
                <td className="px-3 py-2">
                  <div className="font-medium">{m.name}</div>
                  <div className="text-[11px] text-muted-foreground">{m.plugin_id}</div>
                </td>
                <td className="px-3 py-2">
                  <span className={`text-[10px] font-bold uppercase tracking-wider px-2 py-0.5 rounded border ${ACTION_BADGE[m.action] || ""}`}>{m.action}</span>
                </td>
                <td className="px-3 py-2 text-xs font-mono tabular">
                  {m.local_version ? `${m.local_version} → ${m.incoming_version}` : m.incoming_version}
                </td>
                <td className="px-3 py-2 text-xs">
                  <ul className="list-disc pl-4">
                    {(m.reasons || []).map((r, i) => <li key={i}>{r}</li>)}
                  </ul>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {report.missing_deps && report.missing_deps.length > 0 && (
        <div className="border border-amber-300 bg-amber-50 rounded-lg p-3 text-sm">
          <div className="font-bold">Dependencias internas faltantes</div>
          <ul className="list-disc pl-5 text-xs mt-1">
            {report.missing_deps.map((d) => (
              <li key={d.plugin_id}><strong>{d.plugin_id}</strong> requiere: {d.missing_internal_plugins.join(", ")}</li>
            ))}
          </ul>
        </div>
      )}

      <Button
        data-testid="scme-download-report"
        variant="outline" size="sm"
        onClick={() => {
          const blob = new Blob([JSON.stringify(report, null, 2)], { type: "application/json" });
          const url = URL.createObjectURL(blob);
          const a = document.createElement("a");
          a.href = url; a.download = `SCME_REPORT_${report.id}.json`;
          document.body.appendChild(a); a.click(); a.remove();
          URL.revokeObjectURL(url);
        }}
      ><FileText size={14} className="mr-1" /> Descargar informe (JSON)</Button>
    </div>
  );
}

function ImportTab() {
  const inputRef = useRef(null);
  const [file, setFile] = useState(null);
  const [dragOver, setDragOver] = useState(false);
  const [busy, setBusy] = useState(false);
  const [report, setReport] = useState(null);

  const onPickFile = (f) => {
    if (!f) return;
    if (!f.name.toLowerCase().endsWith(".zip")) { toast.error("Debe ser un archivo .zip"); return; }
    if (f.size > 10 * 1024 * 1024) { toast.error("Máx 10MB"); return; }
    setFile(f); setReport(null);
  };

  const analyze = async () => {
    if (!file) return;
    setBusy(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const res = await api.post("/scme/import/analyze", fd, { headers: { "Content-Type": "multipart/form-data" } });
      setReport(res.data);
      toast.success("Análisis completado");
    } catch (e) { toast.error(apiError(e)); }
    finally { setBusy(false); }
  };

  return (
    <div className="space-y-4">
      <Warning />
      <div
        onDragOver={(e) => { e.preventDefault(); setDragOver(true); }}
        onDragLeave={() => setDragOver(false)}
        onDrop={(e) => { e.preventDefault(); setDragOver(false); onPickFile(e.dataTransfer.files?.[0]); }}
        onClick={() => inputRef.current?.click()}
        data-testid="scme-import-dropzone"
        className={`border-2 border-dashed rounded-lg p-8 text-center cursor-pointer transition-colors ${dragOver ? "border-primary bg-primary/5" : "border-border hover:border-foreground/30"}`}
      >
        <Upload size={28} className="mx-auto mb-2 text-muted-foreground" />
        {file ? (
          <>
            <div className="text-sm font-medium">{file.name}</div>
            <div className="text-xs text-muted-foreground">{(file.size / 1024).toFixed(1)} KB</div>
          </>
        ) : (
          <>
            <div className="text-sm font-medium">Suelta aquí un SCME_EXPORT.zip</div>
            <div className="text-xs text-muted-foreground">o haz clic para seleccionar</div>
          </>
        )}
        <input ref={inputRef} type="file" accept=".zip" className="hidden" onChange={(e) => onPickFile(e.target.files?.[0])} />
      </div>

      <div className="flex gap-2">
        <Button data-testid="scme-import-analyze" onClick={analyze} disabled={!file || busy} className="flex-1">
          {busy ? "Analizando…" : "Analizar ZIP"}
        </Button>
        {(file || report) && (
          <Button variant="outline" onClick={() => { setFile(null); setReport(null); if (inputRef.current) inputRef.current.value = ""; }}>
            <RefreshCw size={14} className="mr-1" /> Reiniciar
          </Button>
        )}
      </div>

      {report && <ReportView report={report} />}
    </div>
  );
}

function AuditTab() {
  const [items, setItems] = useState(null);
  const load = () => api.get("/scme/audit").then((r) => setItems(r.data.items)).catch(() => setItems([]));
  useEffect(() => { load(); }, []);
  return (
    <div className="space-y-3">
      <div className="flex justify-between items-center">
        <div className="text-sm text-muted-foreground">Historial de exportaciones y análisis de importación (últimas 50 acciones).</div>
        <Button size="sm" variant="outline" onClick={load}><RefreshCw size={13} className="mr-1" /> Recargar</Button>
      </div>
      <div className="border border-border rounded-lg overflow-hidden">
        <table className="w-full text-sm">
          <thead className="bg-secondary/50 text-xs uppercase tracking-wider text-muted-foreground">
            <tr>
              <th className="text-left px-3 py-2 font-semibold">Fecha</th>
              <th className="text-left px-3 py-2 font-semibold">Acción</th>
              <th className="text-left px-3 py-2 font-semibold">Usuario</th>
              <th className="text-left px-3 py-2 font-semibold">SHA-256</th>
              <th className="text-right px-3 py-2 font-semibold">Tamaño</th>
              <th className="text-left px-3 py-2 font-semibold">Resultado</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-border" data-testid="scme-audit-tbody">
            {items === null ? (
              <tr><td colSpan={6} className="text-center p-4 text-muted-foreground text-sm">Cargando…</td></tr>
            ) : items.length === 0 ? (
              <tr><td colSpan={6} className="text-center p-4 text-muted-foreground text-sm">Sin actividad SCME.</td></tr>
            ) : items.map((it) => (
              <tr key={it.id}>
                <td className="px-3 py-2 text-xs">{new Date(it.at).toLocaleString("es-ES")}</td>
                <td className="px-3 py-2 text-xs font-medium">{it.action}</td>
                <td className="px-3 py-2 text-xs">{it.user_email}</td>
                <td className="px-3 py-2 text-xs font-mono">{(it.sha256 || "").slice(0, 12)}…</td>
                <td className="px-3 py-2 text-xs text-right tabular">{it.size ? `${(it.size / 1024).toFixed(1)} KB` : "—"}</td>
                <td className="px-3 py-2 text-xs">
                  {it.action === "import_analyze" && it.recommendation ? it.recommendation : it.plugin_count ? `${it.plugin_count} plugins` : "—"}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

export default function ScmePanel() {
  const [tab, setTab] = useState("registry");
  const [registry, setRegistry] = useState(null);

  useEffect(() => {
    api.get("/scme/registry").then((r) => setRegistry(r.data)).catch((e) => toast.error(apiError(e)));
  }, []);

  const TABS = [
    { id: "registry", label: "Catálogo local" },
    { id: "export", label: "Exportar" },
    { id: "import", label: "Importar (análisis)" },
    { id: "audit", label: "Historial" },
  ];

  return (
    <div className="space-y-6" data-testid="scme-page">
      <div>
        <h1 className="font-heading text-3xl font-extrabold tracking-tighter flex items-center gap-2">
          <Boxes size={26} /> Ecosistema de Plugins
        </h1>
        <p className="text-sm text-muted-foreground mt-1 max-w-2xl">
          SCME · Sistema Corporativo de Módulos Exportables · v1.0 Operativo. Exportar → Analizar → Informar. Nunca instalar automáticamente.
        </p>
        <a href="/scme/SCME_STANDARD.md" target="_blank" rel="noreferrer" className="inline-flex items-center gap-1 text-xs text-primary hover:underline mt-1">
          <ExternalLink size={11} /> Estándar corporativo SCME
        </a>
      </div>

      <div className="flex flex-wrap gap-1 border-b border-border">
        {TABS.map((t) => (
          <button
            key={t.id}
            onClick={() => setTab(t.id)}
            data-testid={`scme-tab-${t.id}`}
            className={`px-4 py-2 -mb-px border-b-2 text-sm font-medium transition-colors ${
              tab === t.id ? "border-primary text-foreground" : "border-transparent text-muted-foreground hover:text-foreground"
            }`}
          >
            {t.label}
          </button>
        ))}
      </div>

      {tab === "registry" && <RegistryTab registry={registry} />}
      {tab === "export" && <ExportTab registry={registry} />}
      {tab === "import" && <ImportTab />}
      {tab === "audit" && <AuditTab />}
    </div>
  );
}
